<mat-drawer-container class="h-full" autosize="false">
  @if (isNewFlow()) {
    <mat-drawer
      #aiDrawer
      position="end"
      [opened]="isChatOpen()"
      mode="side"
      [style.width.px]="chatLayout.width()"
      [class.transition-none]="chatLayout.isDragging()"
      class="max-w-[90vw] bg-gray-50 border-l border-gray-200"
    >
      <app-ai-agent-chat
        [clientId]="clientId()"
        [isTemplate]="currentRoutePath() === 'templates'"
        (closed)="closeChat()"
        (previewChanged)="handleAiPreviewChanged($event)"
        (interactionCreated)="handleInteractionCreated($event)"
      />
    </mat-drawer>
  }

  <mat-drawer-content
    class="flex flex-col h-full overflow-hidden"
    [style.margin-right.px]="isChatOpen() ? chatLayout.width() : 0"
    [class.transition-none]="chatLayout.isDragging()"
  >
    <div class="h-full flex flex-col">
      <!-- Header bar -->
      <div
        class="flex flex-wrap items-center justify-between gap-2 px-4 md:px-6 py-3 border-b border-gray-200 bg-white shrink-0 min-h-14"
      >
        <div class="flex items-center gap-2 min-w-0">
          <button
            mat-icon-button
            matTooltip="Vorige pagina"
            type="button"
            (click)="goBack()"
          >
            <mat-icon>arrow_back</mat-icon>
          </button>

          <h2 class="text-xl font-medium" [matTooltip]="displayInteraction()?.label ?? ''">
            <span class="max-w-60 truncate block">{{ displayInteraction()?.label }}</span>
          </h2>

          <div class="flex gap-1 flex-wrap">
            @if (displayInteraction()?.version; as version) {
              <span class="bg-gray-300 text-gray-800 py-1 px-2 rounded-full text-xs font-medium">
                v{{ version }}
              </span>
            }

            @if (currentRoutePath() === 'interactions') {
              <span class="bg-gray-300 text-gray-800 py-1 px-2 rounded-full text-xs font-medium">
                Interactie
              </span>
            } @else if (currentRoutePath() === 'templates') {
              <span class="bg-gray-300 text-gray-800 py-1 px-2 rounded-full text-xs font-medium">
                Template
              </span>
            }

            @if (displayInteraction()?.status; as status) {
              <span
                class="py-1 px-2 rounded-full text-xs font-medium"
                [ngClass]="{
                  'bg-gray-300 text-gray-800': status === 'draft',
                  'bg-secondary-200 text-secondary-950': status === 'published',
                }"
              >
                {{ status | mapFrom: statusMapping }}
              </span>
            }
          </div>
        </div>

        <!-- Header Actions -->
        <div class="flex items-center gap-2 shrink-0">
          @if (!isNewFlow()) {
            <button
              mat-button
              type="button"
              (click)="navigateToEditor()"
              class="!text-xs"
              matTooltip="Interactie bewerken in boomweergave"
            >
              <mat-icon class="text-sm size-4 leading-none mr-1">edit</mat-icon>
              Bewerken
            </button>
          }

          @if (isNewFlow() && !isChatOpen()) {
            <button
              mat-button
              type="button"
              (click)="toggleChat()"
              class="!text-xs border border-gray-200 !rounded-lg"
              matTooltip="AI Assistent chat openen"
            >
              <mat-icon class="!text-purple-600 text-sm size-4 leading-none mr-1">auto_awesome</mat-icon>
              AI Assistent
            </button>
          }
        </div>
      </div>

      <!-- Canvas area -->
      <div class="canvas-outer flex-auto overflow-hidden relative select-none" #canvasOuter>
        <div class="canvas-inner" [style.transform]="transformStyle()" [class.pointer-events-none]="isLoading()">
          <!-- SVG edge layer — rendered below node HTML elements -->
          <svg
            class="edge-layer"
            [attr.width]="layout().canvasWidth"
            [attr.height]="layout().canvasHeight"
            style="position: absolute; top: 0; left: 0; overflow: visible; pointer-events: none;"
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="11"
                markerHeight="10"
                markerUnits="userSpaceOnUse"
                refX="0"
                refY="5"
                orient="auto"
              >
                <polygon points="1 1, 10 5, 1 9" fill="#9ca3af" stroke="#9ca3af" stroke-width="2" stroke-linejoin="round" />
              </marker>
              <marker
                id="arrowhead-teal"
                markerWidth="11"
                markerHeight="10"
                markerUnits="userSpaceOnUse"
                refX="0"
                refY="5"
                orient="auto"
              >
                <polygon points="1 1, 10 5, 1 9" fill="#0d9488" stroke="#0d9488" stroke-width="2" stroke-linejoin="round" />
              </marker>
              <marker
                id="arrowhead-orange"
                markerWidth="11"
                markerHeight="10"
                markerUnits="userSpaceOnUse"
                refX="0"
                refY="5"
                orient="auto"
              >
                <polygon points="1 1, 10 5, 1 9" fill="#ea580c" stroke="#ea580c" stroke-width="2" stroke-linejoin="round" />
              </marker>
            </defs>

            @for (edge of computedEdges(); track edge.id) {
              @if (edge.path) {
                <path
                  [attr.d]="edge.path"
                  [attr.stroke]="edge.stroke"
                  [attr.stroke-dasharray]="edge.dashArray || null"
                  [attr.marker-end]="edge.markerEnd"
                  fill="none"
                  stroke-width="2"
                />
              }
            }
          </svg>

          @for (header of layout().subflowHeaders; track header.id) {
            <app-flow-subflow-header
              [style.position]="'absolute'"
              [style.left.px]="header.x"
              [style.top.px]="header.y"
              [style.width.px]="header.width"
              [data]="header"
            />
          }

          @for (node of layout().questionNodes; track node.id) {
            <app-flow-question-node
              [attr.data-node-id]="node.id"
              [style.position]="'absolute'"
              [style.left.px]="node.x"
              [style.top.px]="node.y"
              [style.width.px]="node.width"
              [data]="node.data"
            />
          }

          @for (node of layout().answerNodes; track node.id) {
            <app-flow-answer-node
              [attr.data-node-id]="node.id"
              [style.position]="'absolute'"
              [style.left.px]="node.x"
              [style.top.px]="node.y"
              [style.width.px]="node.width"
              [data]="node.data"
              [gotoInteractionTargetMap]="gotoInteractionTargetMap()"
              [gotoQuestionLabelMap]="gotoQuestionLabelMap()"
              (panToNode)="panToNode($event)"
            />
          }
        </div>

        <!-- Loading overlay -->
        @if (isLoading()) {
          <div class="absolute inset-0 flex items-center justify-center bg-white/50 z-10 pointer-events-none"></div>
        }

        <!-- Empty state -->
        @if (!isLoading() && layout().questionNodes.length === 0) {
          <div
            class="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none"
          >
            <mat-icon class="size-16! text-6xl! text-gray-300">account_tree</mat-icon>
            <p class="text-gray-400 text-sm">Geen flow beschikbaar</p>
            @if (isNewFlow() && isChatOpen()) {
              <p class="text-gray-400 text-xs">Vraag de AI Assistent om een interactie te ontwerpen.</p>
            }
          </div>
        }

        <!-- Zoom controls -->
        <div
          class="flex items-center absolute inset-x-4 bottom-4 gap-2"
          (dblclick)="$event.stopPropagation()"
          (wheel)="$event.stopPropagation()"
        >
          <div class="flex items-center rounded-lg bg-white border border-gray-200 shadow-md overflow-hidden">
            <button
              class="size-9 text-base/9 rounded-lg hover:cursor-pointer hover:bg-gray-100"
              type="button"
              (click)="fitToScreen()"
              matTooltip="Passend maken"
              matTooltipPosition="above"
            >
              <mat-icon inline>fullscreen</mat-icon>
            </button>
          </div>

          <div class="flex items-center rounded-lg bg-white border border-gray-200 shadow-md overflow-hidden">
            <button
              class="size-9 text-base/9 hover:cursor-pointer hover:bg-gray-100"
              type="button"
              (click)="zoomOut()"
              matTooltip="Uitzoomen"
              matTooltipPosition="above"
            >
              <mat-icon inline>remove</mat-icon>
            </button>
            <button
              class="w-15 h-9 px-2 text-sm hover:cursor-pointer hover:bg-gray-100"
              type="button"
              (click)="resetZoom()"
              matTooltip="Zoom resetten"
              matTooltipPosition="above"
            >
              {{ zoomPercent() }}%
            </button>
            <button
              class="size-9 text-base/9 hover:cursor-pointer hover:bg-gray-100"
              type="button"
              (click)="zoomIn()"
              matTooltip="Inzoomen"
              matTooltipPosition="above"
            >
              <mat-icon inline>add</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </mat-drawer-content>
</mat-drawer-container>
